import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, CameraOff, ScanLine, KeyRound } from "lucide-react";

/** Scanner QR sans dépendance : BarcodeDetector natif si dispo, sinon saisie de la référence. */
export const QrScanner = ({ onToken, disabled }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);
  const [active, setActive] = useState(false);
  const [supported, setSupported] = useState(null);
  const [manual, setManual] = useState("");

  useEffect(() => {
    setSupported(typeof window !== "undefined" && "BarcodeDetector" in window);
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = () => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setActive(false);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setActive(true);
      // eslint-disable-next-line no-undef
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      loopRef.current = setInterval(async () => {
        if (!videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes?.length) {
            const raw = codes[0].rawValue;
            stop();
            onToken(raw);
          }
        } catch { /* frame illisible, on continue */ }
      }, 400);
    } catch (e) {
      toast.error("Caméra inaccessible — autorisez l'accès ou saisissez la référence.");
      setActive(false);
    }
  };

  return (
    <div className="border border-[#DEE2E6] bg-white p-4" data-testid="qr-scanner">
      <div className="font-display font-bold text-sm flex items-center gap-2 mb-2">
        <ScanLine className="h-4 w-4 text-[#002FA7]" />Scanner le QR code du client
      </div>

      {supported === false && (
        <div className="text-[11px] text-[#F59E0B] mb-2" data-testid="qr-unsupported">
          Votre navigateur ne sait pas lire les QR codes. Ouvrez cette page depuis Chrome sur
          Android, ou scannez le QR avec l'appareil photo du téléphone : il ouvre directement la
          réservation.
        </div>
      )}

      {supported && (
        <>
          <div className={`relative bg-black overflow-hidden ${active ? "" : "hidden"}`} style={{ aspectRatio: "4/3" }}>
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="qr-video" />
            <div className="absolute inset-0 border-[3px] border-[#FFD700] m-10 pointer-events-none" />
          </div>
          <Button size="sm" variant={active ? "outline" : "default"} disabled={disabled}
                  onClick={active ? stop : start} data-testid="qr-toggle"
                  className={`mt-2 ${active ? "" : "bg-[#002FA7] text-white"}`}>
            {active ? <><CameraOff className="h-4 w-4 mr-1" />Arrêter la caméra</>
                    : <><Camera className="h-4 w-4 mr-1" />Ouvrir la caméra</>}
          </Button>
        </>
      )}

      <div className="mt-3 pt-3 border-t border-[#DEE2E6]">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96] mb-1 flex items-center gap-1">
          <KeyRound className="h-3 w-3" />Ou saisir la référence / le code du QR
        </div>
        <div className="flex gap-2">
          <Input value={manual} onChange={(e) => setManual(e.target.value)}
                 placeholder="R-2609-ABC123 ou code du QR" className="font-mono text-xs"
                 data-testid="qr-manual-input"
                 onKeyDown={(e) => { if (e.key === "Enter" && manual.trim()) { onToken(manual.trim()); setManual(""); } }} />
          <Button size="sm" disabled={!manual.trim() || disabled} data-testid="qr-manual-submit"
                  onClick={() => { onToken(manual.trim()); setManual(""); }}>
            Valider
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Résolution d'une référence saisie à la main → token QR (le check-in accepte les deux). */
export const useCheckin = (onDone) => {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (raw) => {
    setBusy(true);
    try {
      const { data } = await api.post("/chef/minisite/checkin", { qr_token: raw });
      setResult(data);
      if (data.already_arrived) toast.info("Client déjà enregistré comme arrivé");
      else toast.success(`Bienvenue ${data.booking.client_nom} — ${data.booking.unit_label}`);
      onDone?.();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setResult({ error: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return { result, busy, submit, reset: () => setResult(null) };
};
